import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SocialLoginButtonsComponent } from '../../../shared/social-login/social-login-buttons.component';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { LookupsApiService } from '../../../core/api-services/lookups-api.services';
import { Country } from '../../../core/models/country';
import { Institution } from '../../../core/models/institution';
import { Course } from '../../../core/models/course';
import { saIdNumberValidator } from '../../../core/validators/south-african-id.validator';

@Component({
  selector: 'app-registration',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, SocialLoginButtonsComponent],
  templateUrl: './registration.html',
  styleUrl: './registration.scss',
})
export class RegistrationComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly lookupService = inject(LookupsApiService);

  readonly registrationForm: FormGroup;
  countries: Country[] = [];
  institutions: Institution[] = [];
  courses: Course[] = [];
  returnUrl = '/student';
  isSubmitting = false;
  errorMessage = '';
  showPassword = false;
  showConfirmPassword = false;

  constructor() {
    this.registrationForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      emailAddress: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      identityNumber: ['', [Validators.required, saIdNumberValidator()]],
      address: ['', Validators.required],
      countryId: ['', Validators.required],
      institutionId: ['', Validators.required],
      courseId: ['', Validators.required],
    }, { validators: this.passwordMatchValidator });

    this.lookupService.getCountries().subscribe({
      next: countries => (this.countries = countries),
      error: () => undefined,
    });

    const requestedReturnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '';
    this.returnUrl = requestedReturnUrl.startsWith('/') && !requestedReturnUrl.startsWith('//')
      ? requestedReturnUrl
      : '/student';
  }

  onCountryChange(): void {
    const countryId = Number(this.registrationForm.get('countryId')?.value);
    this.institutions = [];
    this.courses = [];
    this.registrationForm.patchValue({ institutionId: '', courseId: '' });

    if (countryId) {
      this.lookupService.getCountryInstitutions(countryId).subscribe({
        next: institutions => (this.institutions = institutions),
        error: () => undefined,
      });
    }
  }

  onInstitutionChange(): void {
    const institutionId = Number(this.registrationForm.get('institutionId')?.value);
    this.courses = [];
    this.registrationForm.patchValue({ courseId: '' });

    if (institutionId) {
      this.lookupService.getInstitutionCourses(institutionId).subscribe({
        next: courses => (this.courses = courses),
        error: () => undefined,
      });
    }
  }

  passwordMatchValidator(form: FormGroup): void {
    const password = form.get('password')?.value;
    const confirmation = form.get('confirmPassword');
    if (password !== confirmation?.value) {
      confirmation?.setErrors({ ...(confirmation.errors ?? {}), mismatch: true });
      return;
    }

    const errors = confirmation?.errors;
    if (errors?.['mismatch']) {
      delete errors['mismatch'];
      confirmation?.setErrors(Object.keys(errors).length ? errors : null);
    }
  }

  get identityNumberError(): string {
    const control = this.registrationForm.get('identityNumber');
    if (!control || !control.touched || !control.errors) {
      return '';
    }
    if (control.errors['required']) {
      return 'ID number is required.';
    }
    if (control.errors['invalidSaIdNumber']) {
      return 'Enter a valid South African ID number (13 digits with a valid checksum).';
    }
    return '';
  }

  submit(): void {
    if (this.registrationForm.invalid || this.isSubmitting) {
      this.registrationForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    this.errorMessage = '';
    const value = this.registrationForm.getRawValue();
    const userName = `${value.firstName}.${value.lastName}`
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9.]/g, '');

    this.authenticationService.register({
      firstName: value.firstName,
      lastName: value.lastName,
      emailAddress: value.emailAddress,
      phoneNumber: value.phoneNumber,
      identityTypeId: 1,
      identityNumber: value.identityNumber,
      institutionId: Number(value.institutionId),
      courseId: Number(value.courseId),
      userName,
      password: value.password,
      address: value.address,
    }).subscribe({
      next: () => this.authenticationService.login({ userName, password: value.password }).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.router.navigateByUrl(this.returnUrl);
        },
        error: () => {
          this.isSubmitting = false;
          this.errorMessage = 'Your account was created, but automatic sign-in failed. Please sign in to continue.';
        },
      }),
      error: error => {
        this.isSubmitting = false;
        this.errorMessage = error.error?.message || 'Registration failed. Please try again.';
      },
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
}
